import React, { useState } from "react";
import {
  Alert,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Form,
  FormFeedback,
  Input,
  Label,
  Row,
  Spinner,
} from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, Navigate } from "react-router-dom";
import ParticlesAuth from "../AuthenticationInner/ParticlesAuth";
import { getBrandLogos } from "../../helpers/brandPortal";
import { APIClient } from "../../helpers/api_helper";
import { mergeTenantMeIntoSession, getLoggedinUser } from "../../helpers/api_helper";
import { POST_ME_GOOGLE_ONBOARDING } from "../../helpers/url_helper";

const api = new APIClient();

/**
 * Pós-signup Google: nome da empresa + senha local (fallback se perder o Google).
 */
const GoogleOnboarding = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation() as { state?: { requiresCheckout?: boolean } };
  const logos = getBrandLogos();
  const session = getLoggedinUser();
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [passwordShow, setPasswordShow] = useState(false);

  const validation = useFormik({
    initialValues: {
      company_name: session?.tenant?.name || "",
      password: "",
      password_confirmation: "",
    },
    validationSchema: Yup.object({
      company_name: Yup.string()
        .min(2, t("auth.googleOnboarding.validation.companyMin"))
        .required(t("auth.googleOnboarding.validation.companyRequired")),
      password: Yup.string()
        .min(8, t("auth.googleOnboarding.validation.passwordMin"))
        .required(t("auth.googleOnboarding.validation.passwordRequired")),
      password_confirmation: Yup.string()
        .oneOf(
          [Yup.ref("password")],
          t("auth.googleOnboarding.validation.passwordMismatch")
        )
        .required(t("auth.googleOnboarding.validation.confirmRequired")),
    }),
    onSubmit: async (values) => {
      setSaving(true);
      setError(null);
      try {
        const envelope: any = await api.create(POST_ME_GOOGLE_ONBOARDING, {
          company_name: values.company_name.trim(),
          password: values.password,
          password_confirmation: values.password_confirmation,
        });
        if (!envelope?.success) {
          setError(
            envelope?.error?.message || t("auth.googleOnboarding.error")
          );
          setSaving(false);
          return;
        }
        mergeTenantMeIntoSession({
          user: envelope.data.user,
          tenant: envelope.data.tenant,
        });
        navigate(
          location.state?.requiresCheckout
            ? "/settings/subscription"
            : "/settings/sending",
          {
            replace: true,
            state: {
              fromRegister: true,
              requiresCheckout: !!location.state?.requiresCheckout,
            },
          }
        );
      } catch (err: any) {
        setError(err?.message || t("auth.googleOnboarding.error"));
        setSaving(false);
      }
    },
  });

  if (!session?.access_token) {
    return <Navigate to="/login" replace />;
  }

  document.title = t("auth.googleOnboarding.documentTitle");

  return (
    <ParticlesAuth>
      <div className="auth-page-content mt-lg-5">
        <Container>
          <Row>
            <Col lg={12}>
              <div className="text-center mt-sm-5 mb-4">
                <img src={logos.dark} alt={logos.alt} height="20" />
              </div>
            </Col>
          </Row>
          <Row className="justify-content-center">
            <Col md={8} lg={6} xl={5}>
              <Card className="mt-4">
                <CardBody className="p-4">
                  <div className="text-center mb-3">
                    <h5 className="text-primary">
                      {t("auth.googleOnboarding.title")}
                    </h5>
                    <p className="text-muted">
                      {t("auth.googleOnboarding.subtitle")}
                    </p>
                  </div>
                  {error ? (
                    <Alert color="danger" role="alert">
                      {error}
                    </Alert>
                  ) : null}
                  <Form
                    onSubmit={(e) => {
                      e.preventDefault();
                      validation.handleSubmit();
                    }}
                  >
                    <div className="mb-3">
                      <Label className="form-label">
                        {t("auth.googleOnboarding.company")}
                      </Label>
                      <Input
                        name="company_name"
                        value={validation.values.company_name}
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        invalid={
                          !!(
                            validation.touched.company_name &&
                            validation.errors.company_name
                          )
                        }
                      />
                      {validation.touched.company_name &&
                      validation.errors.company_name ? (
                        <FormFeedback type="invalid">
                          {String(validation.errors.company_name)}
                        </FormFeedback>
                      ) : null}
                    </div>
                    <div className="mb-3">
                      <Label className="form-label">
                        {t("auth.googleOnboarding.password")}
                      </Label>
                      <div className="position-relative auth-pass-inputgroup">
                        <Input
                          name="password"
                          type={passwordShow ? "text" : "password"}
                          className="pe-5"
                          autoComplete="new-password"
                          value={validation.values.password}
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          invalid={
                            !!(
                              validation.touched.password &&
                              validation.errors.password
                            )
                          }
                        />
                        <button
                          className="btn btn-link position-absolute end-0 top-0 text-decoration-none text-muted"
                          type="button"
                          onClick={() => setPasswordShow(!passwordShow)}
                          aria-label={t("auth.login.togglePassword")}
                        >
                          <i className="ri-eye-fill align-middle" />
                        </button>
                        {validation.touched.password &&
                        validation.errors.password ? (
                          <FormFeedback type="invalid">
                            {String(validation.errors.password)}
                          </FormFeedback>
                        ) : null}
                      </div>
                      <div className="form-text">
                        {t("auth.googleOnboarding.passwordHint")}
                      </div>
                    </div>
                    <div className="mb-3">
                      <Label className="form-label">
                        {t("auth.googleOnboarding.confirmPassword")}
                      </Label>
                      <Input
                        name="password_confirmation"
                        type="password"
                        autoComplete="new-password"
                        value={validation.values.password_confirmation}
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        invalid={
                          !!(
                            validation.touched.password_confirmation &&
                            validation.errors.password_confirmation
                          )
                        }
                      />
                      {validation.touched.password_confirmation &&
                      validation.errors.password_confirmation ? (
                        <FormFeedback type="invalid">
                          {String(validation.errors.password_confirmation)}
                        </FormFeedback>
                      ) : null}
                    </div>
                    <Button
                      color="success"
                      className="w-100"
                      type="submit"
                      disabled={saving}
                    >
                      {saving ? <Spinner size="sm" className="me-2" /> : null}
                      {t("auth.googleOnboarding.submit")}
                    </Button>
                  </Form>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </ParticlesAuth>
  );
};

export default GoogleOnboarding;
